import { ChevronDown } from "lucide-react";
import type { BrandKit, BrandKitColorSettings } from "../models/BrandKit";
import {
  brandColorPositionLabel,
  getBrandColorList,
} from "../lib/brandColors";
import { cn } from "../lib/utils";
import { useRef, useState } from "react";
import { Button } from "./ui/button";
import {
  HEX_INPUT_INVALID_CLASS,
  HEX_INPUT_MAX_LENGTH,
  useHexColorDraft,
} from "./HexColorInput";
import { Input } from "./ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

export interface BrandColorShortcut {
  id: string;
  label: string;
  color: string;
}

export interface BrandKitColorPickerContext {
  brandKits: BrandKit[];
  selectedBrandKitId: string | null;
  onSelectBrandKit: (brandKitId: string) => void;
}

export function getBrandColorShortcuts(
  colors: BrandKitColorSettings | null | undefined
): BrandColorShortcut[] {
  return getBrandColorList(colors).map((color, index) => ({
    id: `brand-color-${index}`,
    label: brandColorPositionLabel(index),
    color,
  }));
}

interface BrandColorShortcutsProps {
  colors?: BrandColorShortcut[];
  onSelect: (color: string) => void;
  brandKitContext?: BrandKitColorPickerContext;
  disabled?: boolean;
  className?: string;
  label?: string;
  size?: "sm" | "md";
}

function BrandKitNamePicker({
  context,
  disabled,
  fallbackLabel,
}: {
  context?: BrandKitColorPickerContext;
  disabled?: boolean;
  fallbackLabel: string;
}) {
  if (!context || context.brandKits.length === 0) {
    return (
      <span className="shrink-0 text-[11px] font-medium leading-none">
        {fallbackLabel}
      </span>
    );
  }

  const selectedBrandKit =
    context.brandKits.find((kit) => kit.id === context.selectedBrandKitId) ??
    context.brandKits[0];
  const selectWidth = `${Math.min(
    Math.max(selectedBrandKit.name.length + 3, 9),
    24
  )}ch`;

  if (context.brandKits.length === 1) {
    return (
      <span
        className="max-w-[9rem] shrink-0 truncate text-[11px] font-medium leading-none text-muted-foreground"
        title={selectedBrandKit.name}
      >
        {selectedBrandKit.name}
      </span>
    );
  }

  return (
    <div className="relative min-w-0 shrink-0 max-w-[9.5rem]">
      <select
        value={selectedBrandKit.id}
        onChange={(event) => context.onSelectBrandKit(event.target.value)}
        disabled={disabled}
        className="h-6 max-w-full appearance-none rounded-sm bg-transparent py-0 pl-0 pr-4 text-[11px] font-medium leading-none text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        style={{ width: selectWidth }}
        aria-label="Select brand kit"
        title={selectedBrandKit.name}
      >
        {context.brandKits.map((kit) => (
          <option key={kit.id} value={kit.id}>
            {kit.name}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-0 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
    </div>
  );
}

export function BrandColorShortcuts({
  colors = [],
  onSelect,
  brandKitContext,
  disabled = false,
  className,
  label = "Brand colors",
  size = "md",
}: BrandColorShortcutsProps) {
  const hasBrandKitPicker = (brandKitContext?.brandKits.length ?? 0) > 0;
  if (colors.length === 0 && !hasBrandKitPicker) return null;

  const swatchSize = size === "sm" ? "h-5 w-5" : "h-6 w-6";

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1.5 text-muted-foreground",
        disabled && "opacity-50",
        className
      )}
      data-testid="brand-color-shortcuts"
    >
      <BrandKitNamePicker
        context={brandKitContext}
        disabled={disabled}
        fallbackLabel={label}
      />
      {colors.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {colors.map((brandColor) => (
            <button
              key={`${brandColor.id}-${brandColor.color}`}
              type="button"
              aria-label={`Use ${brandColor.label} brand color ${brandColor.color}`}
              title={`${brandColor.label}: ${brandColor.color}`}
              disabled={disabled}
              onClick={() => onSelect(brandColor.color)}
              className={cn(
                "rounded-full border border-border/70 shadow-sm ring-offset-background transition hover:scale-105 hover:ring-2 hover:ring-ring/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none",
                swatchSize
              )}
              style={{ backgroundColor: brandColor.color }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface BrandColorPickerSwatchProps {
  id?: string;
  value: string;
  onChange: (color: string) => void;
  brandColors?: BrandColorShortcut[];
  brandKitContext?: BrandKitColorPickerContext;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

function isValidHexColor(color: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(color);
}

const FILLED_COLOR_INPUT_CLASS =
  "cursor-pointer !p-0 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-[3px] [&::-webkit-color-swatch]:border-0 [&::-moz-color-swatch]:rounded-[3px] [&::-moz-color-swatch]:border-0";

interface BrandColorPickerPanelProps {
  value: string;
  onChange: (color: string) => void;
  brandColors?: BrandColorShortcut[];
  brandKitContext?: BrandKitColorPickerContext;
  disabled?: boolean;
  className?: string;
  showActions?: boolean;
  /** Receives the color the field currently spells — not the last live commit. */
  onApply?: (color: string) => void;
  onBack?: () => void;
  autoFocus?: boolean;
  applyLabel?: string;
}

export function BrandColorPickerPanel({
  value,
  onChange,
  brandColors = [],
  brandKitContext,
  disabled = false,
  className,
  showActions = false,
  onApply,
  onBack,
  autoFocus = false,
  applyLabel = "Apply",
}: BrandColorPickerPanelProps) {
  const colorInputRef = useRef<HTMLInputElement>(null);
  const fallbackColor = isValidHexColor(value) ? value : "#000000";
  const hex = useHexColorDraft(fallbackColor, onChange);
  // Apply/Enter act on what the field says, never on a stale committed
  // color behind an unfinished or unparseable draft.
  const canApply = !disabled && hex.parsed !== null;
  const apply = () => {
    if (canApply) onApply?.(hex.parsed!);
  };

  return (
    <div className={cn("relative w-56 space-y-2 p-2", className)}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => colorInputRef.current?.click()}
          disabled={disabled}
          className="h-8 w-8 shrink-0 cursor-pointer rounded-md border border-border shadow-sm transition-all hover:shadow-md active:scale-95 disabled:pointer-events-none disabled:opacity-50"
          style={{ backgroundColor: hex.parsed ?? fallbackColor }}
          aria-label="Choose custom color"
        />
        <input
          type="text"
          value={hex.text}
          onChange={(e) => hex.change(e.target.value)}
          onBlur={() => {
            // With an Apply step, keep an invalid draft visible (Apply stays
            // disabled) instead of silently swapping it for the old color.
            if (!showActions || hex.parsed) hex.settle();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (showActions) {
                apply();
              } else {
                hex.settle();
              }
            } else if (e.key === "Escape") {
              onBack?.();
            }
          }}
          aria-invalid={hex.isInvalid}
          aria-label="Hex color"
          className={cn(
            "h-8 min-w-0 flex-1 rounded-md border bg-background px-2 font-mono text-xs tracking-wide focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            HEX_INPUT_INVALID_CLASS
          )}
          placeholder="#000000"
          maxLength={HEX_INPUT_MAX_LENGTH}
          spellCheck={false}
          autoComplete="off"
          autoFocus={autoFocus}
          disabled={disabled}
        />
      </div>
      <BrandColorShortcuts
        colors={brandColors}
        onSelect={hex.change}
        brandKitContext={brandKitContext}
        disabled={disabled}
        size="sm"
        className="pt-1"
      />
      {showActions && (
        <div className="flex gap-1.5">
          <Button
            size="sm"
            className="h-7 flex-1 text-xs"
            onClick={apply}
            disabled={!canApply}
          >
            {applyLabel}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 flex-1 text-xs"
            onClick={onBack}
            disabled={disabled}
          >
            Back
          </Button>
        </div>
      )}
      {/* The browser anchors its native color-picker popup to this input.
          Kept at the panel's BOTTOM edge so the popup opens below the panel
          instead of covering the shortcuts and Add/Back actions (it lived
          inside the swatch button at the top before). Opened via the swatch
          button's click. */}
      <input
        ref={colorInputRef}
        type="color"
        value={fallbackColor}
        onChange={(e) => hex.change(e.target.value)}
        className="pointer-events-none absolute bottom-0 left-2 h-px w-px opacity-0"
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
      />
    </div>
  );
}

export function BrandColorPickerSwatch({
  id,
  value,
  onChange,
  brandColors = [],
  brandKitContext,
  disabled = false,
  className,
  ariaLabel = "Choose color",
}: BrandColorPickerSwatchProps) {
  const fallbackColor = isValidHexColor(value) ? value : "#000000";
  const [isOpen, setIsOpen] = useState(false);
  const hasBrandKitPicker = (brandKitContext?.brandKits.length ?? 0) > 0;

  if (brandColors.length === 0 && !hasBrandKitPicker) {
    return (
      <Input
        id={id}
        type="color"
        value={fallbackColor}
        onChange={(e) => onChange(e.target.value)}
        className={cn(className, FILLED_COLOR_INPUT_CLASS)}
        disabled={disabled}
        aria-label={ariaLabel}
      />
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          className={cn(
            "shrink-0 rounded-md border border-border shadow-sm ring-offset-background transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
            className
          )}
          disabled={disabled}
          title={ariaLabel}
          aria-label={ariaLabel}
          style={{ backgroundColor: fallbackColor }}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="left"
        sideOffset={8}
        collisionPadding={12}
        className="z-[11000] w-auto p-0"
      >
        <BrandColorPickerPanel
          value={fallbackColor}
          onChange={onChange}
          brandColors={brandColors}
          brandKitContext={brandKitContext}
          disabled={disabled}
        />
      </PopoverContent>
    </Popover>
  );
}
